import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as XLSX from "@redoper1/xlsx-js-style";
import {
  FaEdit,
  FaEnvelope,
  FaSearch,
  FaTrash,
  FaUserCircle,
  FaUserPlus,
  FaUsersCog,
  FaKey,
  FaDownload,
  FaFileExcel,
  FaUpload,
  FaTimes,
  FaIdCard,
  FaShieldAlt,
} from "react-icons/fa";
import api from "../api";
import Modal from "../components/Modal";
import { formatDateOnly, normalizeDateInput } from "../utils/dateOnly";
import { useAuth } from "../AuthContext";

const getInitials = (name = "") => {
  const parts = name.trim().split(" ").filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const formatCpf = (cpf) => {
  if (!cpf) return "—";
  if (cpf.length !== 11) return cpf;
  return `${cpf.slice(0, 3)}.${cpf.slice(3, 6)}.${cpf.slice(6, 9)}-${cpf.slice(9)}`;
};

const defaultFormState = {
  name: "",
  email: "",
  password: "",
  groupId: "",
  birthDate: "",
  sectorId: "",
  cpf: "",
  status: "ACTIVE",
  supervisorIds: [],
};

export default function Users() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [form, setForm] = useState({ ...defaultFormState });
  const [editingId, setEditingId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [groupFilter, setGroupFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("ACTIVE");
  const [sectorModalOpen, setSectorModalOpen] = useState(false);
  const [sectorFormName, setSectorFormName] = useState("");
  const [sectorEditingId, setSectorEditingId] = useState(null);
  const [sectorSaving, setSectorSaving] = useState(false);
  const [sectorError, setSectorError] = useState("");
  const [requiringPasswordChangeId, setRequiringPasswordChangeId] = useState(null);
  const [userCapacity, setUserCapacity] = useState({ limit: null, count: 0, remaining: null });
  const [limitFormValue, setLimitFormValue] = useState("");
  const [limitSaving, setLimitSaving] = useState(false);
  const [limitError, setLimitError] = useState("");
  const [limitSuccess, setLimitSuccess] = useState("");
  const [activeTab, setActiveTab] = useState("list");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const capacityLimit = userCapacity.limit;
  const capacityCount = userCapacity.count;
  const capacityRemaining = userCapacity.remaining;
  const limitReached = capacityLimit !== null && capacityCount >= capacityLimit;
  const isMasterUser = Boolean(user?.isMaster);

  const refreshSectors = useCallback(async () => {
    try {
      const res = await api.get("/sectors");
      const list = res.data || [];
      setSectors(list);
      setForm((prev) => {
        if (!prev.sectorId) return prev;
        const exists = list.some((sector) => String(sector.id) === String(prev.sectorId));
        return exists ? prev : { ...prev, sectorId: "" };
      });
      return list;
    } catch {
      setSectors([]);
      setForm((prev) => ({ ...prev, sectorId: "" }));
      return [];
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const [usersRes, groupsRes, capacityRes] = await Promise.all([
        api.get("/users?includeInactive=true"),
        api.get("/groups"),
        api.get("/users/capacity").catch(() => null),
      ]);

      const userList = Array.isArray(usersRes.data) ? usersRes.data : [];
      const groupList = Array.isArray(groupsRes.data) ? groupsRes.data : [];

      setItems(userList);
      setGroups(groupList);

      if (capacityRes?.data) {
        const rawLimit = capacityRes.data.limit;
        const rawCount = capacityRes.data.count;

        const parsedLimit =
          rawLimit === null || rawLimit === undefined ? null : Number(rawLimit);
        const limitValue =
          parsedLimit !== null && Number.isFinite(parsedLimit) ? Math.trunc(parsedLimit) : null;
        const parsedCount = Number(rawCount);
        const countValue = Number.isFinite(parsedCount) ? Math.trunc(parsedCount) : userList.length;
        const remainingValue =
          limitValue === null ? null : Math.max(limitValue - countValue, 0);

        setUserCapacity({ limit: limitValue, count: countValue, remaining: remainingValue });
        if (isMasterUser) {
          setLimitFormValue(limitValue === null ? "" : String(limitValue));
        }
      } else {
        setUserCapacity((prev) => {
          const limitValue = prev.limit;
          const countValue = userList.length;
          return {
            limit: limitValue,
            count: countValue,
            remaining: limitValue === null ? null : Math.max(limitValue - countValue, 0),
          };
        });
      }
    } catch {
      setItems([]);
      setGroups([]);
      setUserCapacity((prev) => ({
        limit: prev.limit,
        count: 0,
        remaining: prev.limit === null ? null : prev.limit,
      }));
    } finally {
      await refreshSectors();
    }
  }, [refreshSectors, isMasterUser]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!limitSuccess) return undefined;
    const timeout = setTimeout(() => setLimitSuccess(""), 6000);
    return () => clearTimeout(timeout);
  }, [limitSuccess]);

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setForm({ ...defaultFormState });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    const nextValue = name === "cpf" ? value.replace(/\D/g, "") : value;
    setForm({ ...form, [name]: nextValue });
  };

  const handleSupervisorChange = (event) => {
    const selected = Array.from(event.target.selectedOptions || []).map((option) => option.value);
    setForm((prev) => ({ ...prev, supervisorIds: selected }));
  };

  const openSectorManager = () => {
    setSectorModalOpen(true);
    setSectorEditingId(null);
    setSectorFormName("");
    setSectorError("");
  };

  const closeSectorManager = () => {
    setSectorModalOpen(false);
    setSectorEditingId(null);
    setSectorFormName("");
    setSectorError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      const supervisorIds = Array.isArray(form.supervisorIds)
        ? form.supervisorIds
            .map((value) => Number(value))
            .filter((value) => Number.isFinite(value))
        : [];
      const payload = {
        name: form.name,
        email: form.email,
        password: form.password,
        groupId: form.groupId ? Number(form.groupId) : undefined,
        birthDate: form.birthDate || null,
        sectorId: form.sectorId ? Number(form.sectorId) : null,
        cpf: form.cpf || null,
        status: form.status || "ACTIVE",
        directSupervisorIds: supervisorIds,
      };
      if (!form.sectorId) {
        alert("Selecione um setor para o usuário");
        return;
      }
      if (editingId) {
        await api.put(`/users/${editingId}`, payload);
      } else {
        await api.post("/users", payload);
      }
      closeModal();
      await load();
    } catch (error) {
      const msg = error.response?.data?.error || "Erro ao salvar usuário";
      alert(msg);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Remover usuário?")) {
      try {
        await api.delete(`/users/${id}`);
        await load();
      } catch (error) {
        const msg = error.response?.data?.error || "Erro ao remover usuário";
        alert(msg);
      }
    }
  };

  const startEditSector = (sector) => {
    setSectorModalOpen(true);
    setSectorEditingId(sector.id);
    setSectorFormName(sector.name);
    setSectorError("");
  };

  const resetSectorForm = () => {
    setSectorEditingId(null);
    setSectorFormName("");
    setSectorError("");
  };

  const handleSectorSubmit = async (event) => {
    event.preventDefault();
    const name = sectorFormName.trim();
    if (!name) {
      setSectorError("Informe o nome do setor");
      return;
    }
    setSectorSaving(true);
    try {
      if (sectorEditingId) {
        await api.put(`/sectors/${sectorEditingId}`, { name });
      } else {
        await api.post("/sectors", { name });
      }
      const updated = await refreshSectors();
      setSectorError("");
      if (sectorEditingId) {
        const match = updated.find((item) => item.id === sectorEditingId);
        if (match) {
          setSectorFormName(match.name);
        } else {
          resetSectorForm();
        }
      } else {
        setSectorFormName("");
      }
    } catch (error) {
      const msg = error.response?.data?.error || "Erro ao salvar setor";
      setSectorError(msg);
    } finally {
      setSectorSaving(false);
    }
  };

  const handleDeleteSector = async (id) => {
    if (!window.confirm("Remover setor?")) return;
    try {
      await api.delete(`/sectors/${id}`);
      await refreshSectors();
      if (String(form.sectorId) === String(id)) {
        setForm((prev) => ({ ...prev, sectorId: "" }));
      }
      if (sectorEditingId === id) {
        resetSectorForm();
      }
    } catch (error) {
      const msg = error.response?.data?.error || "Erro ao remover setor";
      alert(msg);
    }
  };

  const handleEdit = (user) => {
    setEditingId(user.id);
    setForm({
      name: user.name,
      email: user.email,
      password: "",
      groupId: user.group ? String(user.group.id) : "",
      birthDate: user.birthDate ? normalizeDateInput(user.birthDate) : "",
      sectorId: user.sector?.id ? String(user.sector.id) : "",
      cpf: user.cpf || "",
      status: user.status || "ACTIVE",
      supervisorIds: Array.isArray(user.directSupervisors)
        ? user.directSupervisors.map((supervisor) => String(supervisor.id))
        : [],
    });
    setModalOpen(true);
  };

  const openNew = () => {
    if (limitReached) {
      alert(
        capacityLimit !== null
          ? `Limite de ${capacityLimit} usuário${capacityLimit === 1 ? "" : "s"} atingido. Ajuste o limite nas configurações para cadastrar novos usuários.`
          : "Limite de usuários atingido. Ajuste o limite nas configurações para cadastrar novos usuários.",
      );
      return;
    }
    setEditingId(null);
    setForm({ ...defaultFormState });
    setModalOpen(true);
  };

  const normalizeLookup = (value) => String(value || "").trim().toLowerCase();

  const resolveOptionId = (value, options) => {
    const normalized = normalizeLookup(value);
    if (!normalized) return "";
    const match = options.find(
      (option) => String(option.id) === String(value).trim() || normalizeLookup(option.name) === normalized
    );
    return match ? Number(match.id) : null;
  };

  const addInstructionsSheet = (workbook) => {
    const rows = [
      ["Passo", "Descrição"],
      ["1", "Baixe o template e preencha a aba Usuarios sem alterar os cabeçalhos."],
      ["2", "Campos obrigatórios: Nome, Email, Senha para novos usuários, Grupo e Setor."],
      ["3", "Grupo e Setor podem ser informados pelo nome exatamente como cadastrados ou pelo ID."],
      ["4", "Use Status como ACTIVE ou INACTIVE. Se vazio, o sistema considera ACTIVE."],
      ["5", "Para informar mais de um superior direto, separe e-mails ou nomes por ponto e vírgula (;)."],
      ["6", "Ao importar, e-mails já existentes serão atualizados; e-mails novos serão cadastrados."],
      ["7", "Revise o resumo exibido após a importação para corrigir linhas com erro."],
    ];
    const sheet = XLSX.utils.aoa_to_sheet(rows);
    sheet["!cols"] = [{ wch: 10 }, { wch: 110 }];
    XLSX.utils.book_append_sheet(workbook, sheet, "Passo a passo");
  };

  const buildUserWorksheetRows = (sourceUsers = items) =>
    sourceUsers.map((item) => ({
      Nome: item.name || "",
      Email: item.email || "",
      Senha: "",
      Grupo: item.group?.name || "",
      Setor: item.sector?.name || "",
      "Data de nascimento": item.birthDate ? normalizeDateInput(item.birthDate) : "",
      CPF: item.cpf || "",
      Status: item.status || "ACTIVE",
      "Superiores diretos": Array.isArray(item.directSupervisors)
        ? item.directSupervisors.map((supervisor) => supervisor.email || supervisor.name).join("; ")
        : "",
    }));

  const appendUsersSheet = (workbook, rows) => {
    const sheet = XLSX.utils.json_to_sheet(rows);
    sheet["!cols"] = [
      { wch: 28 }, { wch: 34 }, { wch: 24 }, { wch: 24 }, { wch: 24 },
      { wch: 20 }, { wch: 16 }, { wch: 12 }, { wch: 42 },
    ];
    XLSX.utils.book_append_sheet(workbook, sheet, "Usuarios");
  };

  const handleDownloadTemplate = () => {
    const workbook = XLSX.utils.book_new();
    appendUsersSheet(workbook, [{
      Nome: "Maria Silva",
      Email: "maria.silva@empresa.com",
      Senha: "SenhaTemporaria123",
      Grupo: groups[0]?.name || "Nome do grupo",
      Setor: sectors[0]?.name || "Nome do setor",
      "Data de nascimento": "1990-01-31",
      CPF: "12345678901",
      Status: "ACTIVE",
      "Superiores diretos": "gestor@empresa.com; Coordenador Exemplo",
    }]);
    addInstructionsSheet(workbook);
    XLSX.writeFile(workbook, "template-importacao-usuarios.xlsx", { compression: true });
  };

  const handleExportUsers = () => {
    const workbook = XLSX.utils.book_new();
    appendUsersSheet(workbook, buildUserWorksheetRows());
    addInstructionsSheet(workbook);
    XLSX.writeFile(workbook, "usuarios-exportados.xlsx", { compression: true });
  };

  const handleImportUsers = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array", cellDates: true });
      const sheetName = workbook.SheetNames.find((name) => normalizeLookup(name) === "usuarios") || workbook.SheetNames[0];
      const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { defval: "" });
      const summary = { created: 0, updated: 0, errors: [] };
      const usersByEmail = new Map(items.map((item) => [normalizeLookup(item.email), item]));

      for (const [index, row] of rows.entries()) {
        const line = index + 2;
        const name = String(row.Nome || row.nome || "").trim();
        const email = String(row.Email || row.email || "").trim();
        const password = String(row.Senha || row.senha || "").trim();
        const groupId = resolveOptionId(row.Grupo || row.grupo, groups);
        const sectorId = resolveOptionId(row.Setor || row.setor, sectors);
        const existingUser = usersByEmail.get(normalizeLookup(email));

        if (!name || !email || !groupId || !sectorId || (!existingUser && !password)) {
          summary.errors.push(`Linha ${line}: preencha Nome, Email, Grupo, Setor e Senha para novos usuários.`);
          continue;
        }

        const supervisors = String(row["Superiores diretos"] || row.superiores || "")
          .split(";")
          .map((value) => normalizeLookup(value))
          .filter(Boolean);
        const directSupervisorIds = items
          .filter((item) => supervisors.includes(normalizeLookup(item.email)) || supervisors.includes(normalizeLookup(item.name)))
          .map((item) => Number(item.id));
        const birthDateValue = row["Data de nascimento"] || row.birthDate || row.Nascimento || "";
        const birthDate = birthDateValue instanceof Date
          ? birthDateValue.toISOString().slice(0, 10)
          : String(birthDateValue || "").trim() || null;
        const status = String(row.Status || row.status || "ACTIVE").trim().toUpperCase() === "INACTIVE" ? "INACTIVE" : "ACTIVE";
        const payload = {
          name, email, password, groupId, birthDate, sectorId,
          cpf: String(row.CPF || row.cpf || "").replace(/\D/g, "") || null,
          status, directSupervisorIds,
        };

        try {
          if (existingUser) {
            if (!payload.password) delete payload.password;
            await api.put(`/users/${existingUser.id}`, payload);
            summary.updated += 1;
          } else {
            await api.post("/users", payload);
            summary.created += 1;
          }
        } catch (error) {
          summary.errors.push(`Linha ${line}: ${error.response?.data?.error || "erro ao salvar usuário"}`);
        }
      }
      setImportResult(summary);
      await load();
    } catch {
      setImportResult({ created: 0, updated: 0, errors: ["Não foi possível ler o arquivo. Use um .xlsx válido."] });
    } finally {
      setImporting(false);
    }
  };

  const filteredUsers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const filtered = items.filter((user) => {
      const matchesTerm = term
        ? user.name.toLowerCase().includes(term) || user.email.toLowerCase().includes(term)
        : true;
      const matchesGroup =
        groupFilter === "all" || (user.group && String(user.group.id) === groupFilter);
      const matchesStatus =
        statusFilter === "all" || (user.status || "ACTIVE") === statusFilter;
      return matchesTerm && matchesGroup && matchesStatus;
    });

    return filtered.sort((a, b) =>
      a.name.localeCompare(b.name, "pt-BR", { sensitivity: "base", ignorePunctuation: true })
    );
  }, [items, searchTerm, groupFilter, statusFilter]);

  const supervisorOptions = useMemo(() => {
    const candidates = items.filter(
      (candidate) =>
        (!editingId || candidate.id !== editingId) && (candidate.status || "ACTIVE") === "ACTIVE"
    );
    return candidates
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [items, editingId]);

  const totalUsers = items.filter((user) => (user.status || "ACTIVE") === "ACTIVE").length;
  const groupCount = groups.length;
  const withoutGroup = items.filter((user) => !user.group).length;
  const pendingPasswordChanges = items.filter((user) => user.mustChangePassword).length;
  const groupUserCount = useMemo(() => {
    return items.reduce((acc, user) => {
      if (user.group?.id) {
        acc[user.group.id] = (acc[user.group.id] || 0) + 1;
      }
      return acc;
    }, {});
  }, [items]);
  const busiestGroup = useMemo(() => {
    if (!groups.length) return null;
    return groups
      .slice()
      .sort((a, b) => (groupUserCount[b.id] || 0) - (groupUserCount[a.id] || 0))[0];
  }, [groups, groupUserCount]);

  const handleRequirePasswordChange = async (user) => {
    if (!user) return;
    if (
      !window.confirm(
        `Solicitar que ${user.name} altere a senha no próximo acesso?`
      )
    ) {
      return;
    }
    setRequiringPasswordChangeId(user.id);
    try {
      await api.post(`/users/${user.id}/require-password-change`);
      await load();
    } catch (error) {
      const msg = error.response?.data?.error || "Não foi possível solicitar a troca de senha";
      alert(msg);
    } finally {
      setRequiringPasswordChangeId(null);
    }
  };

  const isEditing = Boolean(editingId);

  const handleLimitSubmit = async (event) => {
    event.preventDefault();
    if (!isMasterUser) return;

    const trimmed = limitFormValue.trim();
    const numericValue = trimmed === "" ? null : Number(trimmed.replace(/,/g, "."));

    if (numericValue !== null) {
      if (!Number.isFinite(numericValue) || numericValue < 0) {
        setLimitError("Informe um número válido maior ou igual a zero.");
        setLimitSuccess("");
        return;
      }
    }

    setLimitSaving(true);
    setLimitError("");
    setLimitSuccess("");

    try {
      const payload = { limit: numericValue === null ? null : Math.trunc(numericValue) };
      const response = await api.put("/system-settings/user-limit", payload);
      const nextLimit = response?.data?.limit;
      const normalized =
        nextLimit === null || nextLimit === undefined
          ? null
          : Number.isFinite(Number(nextLimit))
          ? Math.trunc(Number(nextLimit))
          : null;
      setLimitFormValue(normalized === null ? "" : String(normalized));
      setLimitSuccess("Limite de usuários atualizado com sucesso.");
      await load();
    } catch (error) {
      const message =
        error.response?.data?.error || "Não foi possível atualizar o limite de usuários.";
      setLimitError(message);
    } finally {
      setLimitSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      <header className="flex flex-col gap-4 rounded-3xl bg-gradient-to-r from-[#071f54] via-[#0b3474] to-[#0d4d91] p-5 text-white shadow-sm lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-blue-200">Gestão de identidades</p>
          <h1 className="text-3xl font-semibold text-white">Usuários da plataforma</h1>
        </div>
        <div className="flex flex-col items-stretch gap-2 text-right">
          <button
            onClick={openNew}
            disabled={limitReached}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-medium shadow-lg transition ${
              limitReached
                ? "cursor-not-allowed bg-emerald-300 text-emerald-900/70 shadow-none"
                : "bg-white text-[#0b3474] hover:bg-blue-50"
            }`}
          >
            <FaUserPlus />
            Novo usuário
          </button>
          <p className="text-xs text-blue-100">
            {capacityLimit !== null
              ? limitReached
                ? `Limite de ${capacityLimit} usuário${capacityLimit === 1 ? "" : "s"} atingido.`
                : `${capacityCount} de ${capacityLimit} usuário${capacityLimit === 1 ? "" : "s"} utilizados${
                    capacityRemaining !== null
                      ? ` • ${capacityRemaining} vaga${capacityRemaining === 1 ? "" : "s"} restante${
                          capacityRemaining === 1 ? "" : "s"
                        }`
                      : ""
                  }.`
              : `${capacityCount} usuário${capacityCount === 1 ? "" : "s"} ativos. Limite não configurado.`}
          </p>
        </div>
      </header>

      {isMasterUser && (
        <section className="space-y-4 rounded-3xl border border-emerald-100 bg-emerald-50/70 p-6 shadow-sm">
          <header className="space-y-1">
            <h2 className="text-xl font-semibold text-emerald-900">Limite de usuários</h2>
            <p className="text-sm text-emerald-700">
              Defina a quantidade máxima de usuários (exceto o master) que podem ser cadastrados na
              plataforma.
            </p>
          </header>
          <form onSubmit={handleLimitSubmit} className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <label className="flex-1 space-y-1 text-sm">
              <span className="font-medium text-emerald-900">Quantidade permitida</span>
              <input
                type="number"
                min="0"
                step="1"
                value={limitFormValue}
                onChange={(event) => {
                  setLimitFormValue(event.target.value);
                  setLimitError("");
                  setLimitSuccess("");
                }}
                placeholder="Sem limite"
                className="w-full rounded-lg border border-emerald-200 bg-white px-3 py-2 text-sm text-emerald-900 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200"
              />
              <span className="text-xs text-emerald-700">
                Deixe em branco para remover o limite de usuários.
              </span>
            </label>
            <div className="flex gap-2">
              {limitFormValue !== "" && (
                <button
                  type="button"
                  onClick={() => {
                    setLimitFormValue("");
                    setLimitError("");
                    setLimitSuccess("");
                  }}
                  className="rounded-full border border-emerald-200 px-4 py-2 text-sm font-medium text-emerald-700 transition hover:border-emerald-300 hover:bg-emerald-100/60"
                >
                  Limpar valor
                </button>
              )}
              <button
                type="submit"
                disabled={limitSaving}
                className="rounded-full bg-emerald-600 px-5 py-2 text-sm font-semibold text-white shadow-md transition hover:bg-emerald-700 disabled:opacity-60"
              >
                {limitSaving ? "Salvando..." : "Aplicar limite"}
              </button>
            </div>
          </form>
          {limitError && <p className="text-sm text-rose-600">{limitError}</p>}
          {limitSuccess && <p className="text-sm text-emerald-700">{limitSuccess}</p>}
        </section>
      )}

      <nav className="flex flex-wrap gap-2 rounded-2xl border border-blue-100 bg-blue-50/60 p-2 shadow-sm">
        <button type="button" onClick={() => setActiveTab("list")} className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${activeTab === "list" ? "bg-[#0b3474] text-white shadow-md" : "text-slate-600 hover:bg-white"}`}>Usuários</button>
        <button type="button" onClick={() => setActiveTab("importExport")} className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${activeTab === "importExport" ? "bg-[#0b3474] text-white shadow-md" : "text-slate-600 hover:bg-white"}`}>Importação / Exportação</button>
      </nav>

      {activeTab === "list" ? (
        <>
      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <div className="flex items-center gap-3 rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-emerald-800 shadow-sm">
          <FaUserCircle className="text-xl" />
          <div>
            <p className="text-xs uppercase tracking-wide text-emerald-500">Usuários ativos</p>
            <p className="text-2xl font-semibold">{totalUsers}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-blue-100 bg-blue-50 p-4 text-blue-800 shadow-sm">
          <FaUsersCog className="text-xl" />
          <div>
            <p className="text-xs uppercase tracking-wide text-blue-500">Grupos disponíveis</p>
            <p className="text-2xl font-semibold">{groupCount}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-amber-100 bg-amber-50 p-4 text-amber-700 shadow-sm">
          <FaSearch className="text-xl" />
          <div>
            <p className="text-xs uppercase tracking-wide text-amber-500">Sem grupo</p>
            <p className="text-2xl font-semibold">{withoutGroup}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-slate-700 shadow-sm">
          <FaEdit className="text-xl text-slate-500" />
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-400">Grupo destaque</p>
            <p className="text-sm font-medium text-slate-700">
              {busiestGroup
                ? `${busiestGroup.name} (${groupUserCount[busiestGroup.id] || 0} usuários)`
                : "Adicione grupos para visualizar"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-amber-100 bg-amber-50 p-4 text-amber-700 shadow-sm">
          <FaKey className="text-xl" />
          <div>
            <p className="text-xs uppercase tracking-wide text-amber-500">Trocas de senha pendentes</p>
            <p className="text-2xl font-semibold">{pendingPasswordChanges}</p>
          </div>
        </div>
      </section>

      <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative w-full sm:max-w-sm">
            <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Buscar por nome ou e-mail"
              className="w-full rounded-full border border-slate-200 bg-white py-2 pl-10 pr-4 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200"
            />
          </div>
          <select
            value={groupFilter}
            onChange={(event) => setGroupFilter(event.target.value)}
            className="w-full rounded-full border border-slate-200 bg-white py-2 pl-4 pr-8 text-sm text-slate-600 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 sm:w-60"
          >
            <option value="all">Todos os grupos</option>
            {groups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            className="w-full rounded-full border border-slate-200 bg-white py-2 pl-4 pr-8 text-sm text-slate-600 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 sm:w-44"
          >
            <option value="ACTIVE">Ativos</option>
            <option value="INACTIVE">Inativos</option>
            <option value="all">Todos</option>
          </select>
        </div>
        <p className="text-sm text-slate-500 text-right">
          {filteredUsers.length} {filteredUsers.length === 1 ? "usuário encontrado" : "usuários encontrados"}
        </p>
      </div>

      <section className="grid gap-4 xl:grid-cols-2">
        {filteredUsers.length ? (
          filteredUsers.map((user) => (
            <article
              key={user.id}
              className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-md"
            >
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-sm font-semibold uppercase text-emerald-700 shadow-inner">
                  {getInitials(user.name)}
                </div>
                <div className="flex-1 space-y-3">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h2 className="text-lg font-semibold text-slate-900">{user.name}</h2>
                      <p className="flex items-center gap-2 text-sm text-slate-500">
                        <FaEnvelope className="text-slate-400" />
                        {user.email}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <span
                        className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium ${
                          user.status === "INACTIVE"
                            ? "border-rose-200 bg-rose-50 text-rose-600"
                            : "border-emerald-100 bg-emerald-50 text-emerald-600"
                        }`}
                      >
                        {user.status === "INACTIVE" ? "Inativo" : "Ativo"}
                      </span>
                      <span className="inline-flex items-center rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-600">
                        {user.group ? user.group.name : "Sem grupo"}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
                    <span>ID #{String(user.id).padStart(3, "0")}</span>
                    <span>•</span>
                    <span>{user.group ? `${groupUserCount[user.group.id] || 1} usuários no grupo` : "Aguardando associação"}</span>
                    {user.mustChangePassword && (
                      <>
                        <span>•</span>
                        <span className="text-amber-600">Troca de senha solicitada</span>
                      </>
                    )}
                  </div>
                  <dl className="grid gap-3 pt-4 text-sm text-slate-600 sm:grid-cols-2">
                    <div>
                      <dt className="text-xs uppercase tracking-wide text-slate-400">Setor</dt>
                      <dd className="font-medium text-slate-700">{user.sector?.name || "—"}</dd>
                    </div>
                    <div>
                      <dt className="text-xs uppercase tracking-wide text-slate-400">CPF</dt>
                      <dd className="font-medium text-slate-700">{formatCpf(user.cpf)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs uppercase tracking-wide text-slate-400">Nascimento</dt>
                      <dd className="font-medium text-slate-700">{formatDateOnly(user.birthDate)}</dd>
                    </div>
                    <div className="sm:col-span-2">
                      <dt className="text-xs uppercase tracking-wide text-slate-400">Superiores diretos</dt>
                      <dd className="mt-1 flex flex-wrap gap-2">
                        {Array.isArray(user.directSupervisors) && user.directSupervisors.length ? (
                          user.directSupervisors.map((supervisor) => (
                            <span
                              key={supervisor.id}
                              className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600"
                            >
                              {supervisor.name}
                            </span>
                          ))
                        ) : (
                          <span className="text-slate-500">—</span>
                        )}
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-2">
                <button
                  onClick={() => handleRequirePasswordChange(user)}
                  className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition ${
                    user.mustChangePassword
                      ? "cursor-not-allowed border-amber-200 bg-amber-50 text-amber-600"
                      : "border-amber-200 text-amber-600 hover:border-amber-300 hover:bg-amber-50"
                  }`}
                  disabled={user.mustChangePassword || requiringPasswordChangeId === user.id}
                >
                  <FaKey />
                  {user.mustChangePassword
                    ? "Solicitado"
                    : requiringPasswordChangeId === user.id
                    ? "Solicitando..."
                    : "Solicitar troca de senha"}
                </button>
                <button
                  onClick={() => handleEdit(user)}
                  className="inline-flex items-center gap-2 rounded-full border border-emerald-200 px-4 py-2 text-sm font-medium text-emerald-600 transition hover:border-emerald-300 hover:bg-emerald-50"
                >
                  <FaEdit />
                  Editar
                </button>
                <button
                  onClick={() => handleDelete(user.id)}
                  className="inline-flex items-center gap-2 rounded-full border border-rose-200 px-4 py-2 text-sm font-medium text-rose-600 transition hover:border-rose-300 hover:bg-rose-50"
                >
                  <FaTrash />
                  Remover
                </button>
              </div>
            </article>
          ))
        ) : (
          <div className="col-span-full rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-slate-500">
            Nenhum usuário corresponde aos filtros aplicados.
          </div>
        )}
      </section>

        </>
      ) : (
        <section className="space-y-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <header className="space-y-2">
            <p className="text-xs uppercase tracking-[0.3em] text-emerald-500">Excel</p>
            <h2 className="text-2xl font-semibold text-slate-900">Importar e exportar usuários</h2>
            <p className="text-sm text-slate-600">Use o template oficial para cadastrar ou atualizar usuários em lote sem sair da aba de usuários.</p>
          </header>
          <div className="grid gap-4 lg:grid-cols-3">
            <button type="button" onClick={handleDownloadTemplate} className="flex items-center gap-3 rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-left text-emerald-800 transition hover:border-emerald-200 hover:bg-emerald-100"><FaFileExcel className="text-2xl" /><span><strong className="block">Baixar template</strong><small>Planilha com exemplo e aba de passo a passo.</small></span></button>
            <button type="button" onClick={handleExportUsers} className="flex items-center gap-3 rounded-2xl border border-blue-100 bg-blue-50 p-4 text-left text-blue-800 transition hover:border-blue-200 hover:bg-blue-100"><FaDownload className="text-2xl" /><span><strong className="block">Exportar usuários</strong><small>Gere um Excel com os usuários cadastrados.</small></span></button>
            <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-amber-100 bg-amber-50 p-4 text-left text-amber-800 transition hover:border-amber-200 hover:bg-amber-100"><FaUpload className="text-2xl" /><span><strong className="block">Importar planilha</strong><small>{importing ? "Importando..." : "Selecione um arquivo .xlsx preenchido."}</small></span><input type="file" accept=".xlsx,.xls" onChange={handleImportUsers} disabled={importing} className="hidden" /></label>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
            <h3 className="text-lg font-semibold text-slate-800">Passo a passo</h3>
            <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm text-slate-600">
              <li>Baixe o template em Excel e preencha a aba <strong>Usuarios</strong>.</li>
              <li>Mantenha os cabeçalhos originais; Nome, Email, Grupo e Setor são obrigatórios.</li>
              <li>Informe Senha para novos usuários. Para e-mails já cadastrados, a senha pode ficar vazia.</li>
              <li>Use Grupo e Setor pelo nome cadastrado ou pelo ID. Status aceita ACTIVE ou INACTIVE.</li>
              <li>Separe múltiplos superiores diretos com ponto e vírgula (;), usando nome ou e-mail.</li>
              <li>Importe o arquivo e confira o resumo de criados, atualizados e erros por linha.</li>
            </ol>
          </div>
          {importResult && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-700 shadow-sm">
              <p className="font-semibold text-slate-900">Resultado: {importResult.created} criado(s), {importResult.updated} atualizado(s), {importResult.errors.length} erro(s).</p>
              {importResult.errors.length > 0 && (<ul className="mt-3 max-h-48 list-disc space-y-1 overflow-y-auto pl-5 text-rose-600">{importResult.errors.map((error, index) => (<li key={`${error}-${index}`}>{error}</li>))}</ul>)}
            </div>
          )}
        </section>
      )}

      <Modal
        open={modalOpen}
        onClose={closeModal}
        size="max-w-2xl"
        panelClassName="users-form-modal-panel"
      >
        <div className="users-form-modal">
          <header className="space-y-1">
            <h2 className="text-2xl font-semibold text-slate-900">
              {isEditing ? "Editar usuário" : "Novo usuário"}
            </h2>
            <p className="text-sm text-slate-500">
              Preencha os dados do colaborador e associe-o a um grupo com as permissões corretas.
            </p>
          </header>

          <form onSubmit={handleSubmit} className="users-form-modal-form">
            <div className="users-form-modal-body space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="space-y-1 text-sm">
                <span className="font-medium text-slate-700">Nome</span>
                <input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Nome completo"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                  required
                />
              </label>
              <label className="space-y-1 text-sm">
                <span className="font-medium text-slate-700">E-mail</span>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="usuario@empresa.com"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                  required
                />
              </label>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <label className="space-y-1 text-sm">
                <span className="font-medium text-slate-700">Data de nascimento</span>
                <input
                  name="birthDate"
                  type="date"
                  value={form.birthDate}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                />
              </label>
              <label className="space-y-1 text-sm">
                <span className="font-medium text-slate-700">Setor</span>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <select
                    name="sectorId"
                    value={form.sectorId}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                    required
                  >
                    <option value="">Selecione um setor</option>
                    {sectors.map((sector) => (
                      <option key={sector.id} value={sector.id}>
                        {sector.name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={openSectorManager}
                    className="whitespace-nowrap rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 transition hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700"
                  >
                    Gerenciar setores
                  </button>
                </div>
                {sectors.length === 0 && (
                  <p className="text-xs text-rose-600">Cadastre ao menos um setor antes de criar usuários.</p>
                )}
              </label>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <label className="space-y-1 text-sm">
                <span className="font-medium text-slate-700">CPF</span>
                <input
                  name="cpf"
                  value={form.cpf}
                  onChange={handleChange}
                  placeholder="Somente números"
                  maxLength={11}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                />
              </label>
              <label className="space-y-1 text-sm">
                <span className="font-medium text-slate-700">Senha</span>
                <input
                  name="password"
                  type="password"
                  value={form.password}
                  onChange={handleChange}
                  placeholder={isEditing ? "Deixe em branco para manter" : "Defina uma senha temporária"}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                  {...(isEditing ? {} : { required: true })}
                />
              </label>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <label className="space-y-1 text-sm">
                <span className="font-medium text-slate-700">Grupo</span>
                <select
                  name="groupId"
                  value={form.groupId}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                  required
                >
                  <option value="">Selecione um grupo</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="space-y-1 text-sm">
                <span className="font-medium text-slate-700">Status</span>
                <select
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                >
                  <option value="ACTIVE">Ativo</option>
                  <option value="INACTIVE">Inativo</option>
                </select>
              </label>
            </div>

            <label className="space-y-1 text-sm">
              <span className="font-medium text-slate-700">Superiores diretos</span>
              <select
                name="supervisorIds"
                multiple
                value={form.supervisorIds}
                onChange={handleSupervisorChange}
                size={Math.min(4, Math.max(supervisorOptions.length || 0, 3))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200"
              >
                {supervisorOptions.length ? (
                  supervisorOptions.map((candidate) => (
                    <option key={candidate.id} value={candidate.id}>
                      {candidate.name}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>
                    Nenhum usuário disponível
                  </option>
                )}
              </select>
              <p className="text-xs text-slate-500">
                Escolha os gestores que poderão supervisionar diretamente este usuário.
              </p>
            </label>

            </div>

            <div className="users-form-modal-actions">
              <button
                type="button"
                onClick={closeModal}
                className="users-form-cancel-button"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="users-form-save-button"
              >
                {isEditing ? "Salvar alterações" : "Criar usuário"}
              </button>
            </div>
          </form>
        </div>
      </Modal>
      <Modal open={sectorModalOpen} onClose={closeSectorManager}>
        <div className="space-y-5">
          <header className="space-y-1">
            <h2 className="text-2xl font-semibold text-slate-900">Gerenciar setores</h2>
            <p className="text-sm text-slate-500">
              Cadastre, renomeie ou remova setores utilizados para vincular usuários e processos.
            </p>
          </header>
          <form onSubmit={handleSectorSubmit} className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <label className="flex-1 space-y-1 text-sm">
              <span className="font-medium text-slate-700">
                {sectorEditingId ? "Editar setor" : "Novo setor"}
              </span>
              <input
                value={sectorFormName}
                onChange={(event) => setSectorFormName(event.target.value)}
                placeholder="Ex.: Financeiro"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200"
              />
            </label>
            <div className="flex gap-2">
              {sectorEditingId && (
                <button
                  type="button"
                  onClick={resetSectorForm}
                  className="rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-slate-300 hover:bg-slate-50"
                >
                  Cancelar
                </button>
              )}
              <button
                type="submit"
                className="rounded-full bg-emerald-600 px-5 py-2 text-sm font-semibold text-white shadow-md transition hover:bg-emerald-700 disabled:opacity-60"
                disabled={sectorSaving}
              >
                {sectorSaving
                  ? "Salvando..."
                  : sectorEditingId
                  ? "Atualizar setor"
                  : "Adicionar setor"}
              </button>
            </div>
          </form>
          {sectorError && <p className="text-sm text-rose-600">{sectorError}</p>}
          <div className="max-h-64 overflow-y-auto rounded-2xl border border-slate-200 bg-slate-50 p-3">
            {sectors.length ? (
              <ul className="space-y-2">
                {sectors.map((sector) => (
                  <li
                    key={sector.id}
                    className="flex items-center justify-between rounded-xl bg-white px-3 py-2 shadow-sm"
                  >
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{sector.name}</p>
                      <p className="text-xs text-slate-400">ID #{String(sector.id).padStart(3, "0")}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => startEditSector(sector)}
                        className="rounded-full border border-emerald-200 px-3 py-1 text-xs font-medium text-emerald-600 transition hover:border-emerald-300 hover:bg-emerald-50"
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteSector(sector.id)}
                        className="rounded-full border border-rose-200 px-3 py-1 text-xs font-medium text-rose-600 transition hover:border-rose-300 hover:bg-rose-50"
                      >
                        Remover
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-slate-500">Nenhum setor cadastrado ainda.</p>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}
